<template>
	<div class="pb-16">
		<Mango @update:data="setKit" @load-error="onKitLoadError" class="flex flex-col w-full items-center" :query="{ depthLimit: 2 }">
			<Spinner v-if="!kit?.id && !kitLoadError" />
			<div v-else-if="kitLoadError && !kit?.id" class="w-full h-full flex items-center justify-center">
				<div class="p-16 mt-16 text-center">
					<h1 class="text-ihh-midnight-gray text-2xl font-semibold">This birth kit couldn't be loaded.</h1>
					<p class="text-ihh-midnight-gray mt-2">Check your connection and refresh the page.</p>
				</div>
			</div>
			<div v-else-if="!kit.products?.length" class="w-full h-full flex items-center justify-center">
				<div class="p-16 mt-16">
					<h1 class="text-ihh-midnight-gray text-2xl font-semibold text-center">There are no items in this kit.</h1>
				</div>
			</div>
			<template v-if="kit?.id">
				<div class="w-full max-w-8xl mb-12">
					<div class="p-4" style="font-family: 'Montserrat', sans-serif">
						<div class="mb-2">
							<!-- Title and Image -->
							<div class="flex flex-col md:flex-row items-center justify-center w-full">
								<div id="kit-title" class="flex flex-col items-center md:items-end w-full space-y-2 md:order-2" :class="!showKitImage ? 'mt-4 mb-8' : ''">
									<div class="text-2xl sm:text-4xl font-semibold text-ihh-midnight-gray text-center md:text-right">
										{{ kit.name?.replace(/ Custom Birth Kit/gi, '').trim() }}
									</div>
									<div v-if="!kit?.noCustomBKLabel" class="text-lg sm:text-2xl text-ihh-midnight-gray bg-ihh-light-gray px-3 py-1 rounded-lg shadow-lg">
										Custom Birth Kit
									</div>
								</div>
								<div v-if="showKitImage" class="w-full md:max-w-80 flex justify-center py-4 mt-4 md:mt-0 md:order-1">
									<img :src="kit.image.url" @error="kitImageError = true" class="max-w-full max-h-48 md:w-72 md:h-72 rounded-lg object-contain" />
								</div>
							</div>

							<!-- Custom Birth Kit Message -->
							<div v-if="showCustomBKMessage && !kit?.noCustomBKLabel" class="mx-4 md:mx-4 lg:mx-0 2xl:mx-0 mb-12">
								<div class="w-full rounded-xl bg-ihh-light-gray p-10 md:mt-0 text-base lg:text-lg text-ihh-midnight-gray shadow-lg space-y-2 relative">
									<button
										class="absolute top-3 right-3 text-ihh-midnight-gray hover:text-ihh-midnight-gray/80 transition-colors duration-200"
										@click="hideCustomBKMessage"
										aria-label="Close message"
									>
										<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
											<path
												fill-rule="evenodd"
												d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
												clip-rule="evenodd"
											/>
										</svg>
									</button>
									<p>
										<strong>Welcome!</strong> This page features the custom birth kit designed by
										{{ kit?.name?.replace(/ Custom Birth Kit/gi, '').trim() }}. They have thoughtfully selected each item to support you during your
										upcoming birth. In line with their recommendations, you are welcome to customize the kit to fit your preferences.
									</p>
									<p v-if="kit?.products?.some((p) => p.optional)">
										They have also included a list of Optional Items at the bottom of the page that you may choose to add.
									</p>
									<p>
										If you have any questions, please don’t hesitate to
										<router-link to="/help-center" class="text-ihh-dark-salmon font-semibold hover:text-ihh-salmon transition-colors duration-200"
											>reach out</router-link
										>, we are here to help!
									</p>
								</div>
							</div>

							<!-- Download Order Form -->
							<div class="flex flex-col w-full">
								<div class="w-full flex justify-between items-center">
									<!-- Download Order Form -->
									<div>
										<button @click="downloadContentOnPage" :class="hasOutOfStockItems ? 'pb-0' : 'pb-0'" class="flex items-center gap-2">
											<img
												src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAAAsTAAALEwEAmpwYAAAC6UlEQVR4nO2az2sTURDHn4Io9uKPi3pR0It401P/CEUEqSJYobKd2RLSzKRQe5lJattDKf66+AvsPyFeVMSbSPWip6KXot4V6qEa5SVS05qN283W7Mb5wEDYzZud+b59834kzhmGYRiGYfxDSkOyh0DuM8pHAv3AqPfGcWa3+x8IgjvbCPQ1o/5oNgJd8Pdcr0OoA+uTXxUBK2ddr0Mg01ECMOqU63UYdC5SANA51+uwCaD2BrANAbUawFYE1WYBtmlQbR3AthBSWwmyLYXV9gJsmyG13SD3yna4FMoRDrVEIBPloNKfdDvs29Z9gIyWg8nDLg9QqOcZZPl3YlJjkJsisjWuAP67hHqr3nb1niwzyDmXZcZG5ACDfml93ifzPrG/CdBIXuYjfHwuh7LPZRUa1gttzvt8gg8Y5Hr0oaheY5Tb7X1k+C0g//q3C/5XLya51yTSgMvjEOAUzAs0Glzd77IMhXJpTfFKzaTmfbs8wChBuiJIjVALXUlGfFUGOcUoyqhD/ne9OO18wOmIED/58ZHpvQRyuRFr5aRzbktnyQ/KDkJ9smYcgn4aG5HjMUUodiZCPflinGf5mHxs62aMx4XCje2JBeBGr7eait7E9eFXcUkF8G3jPodQ37YsnKDSgQD6MjLAcPLgZopAIONx/ReH5VAbXy8SC0CgS5G9s26Nn6YIG0m+7juo9Ef70iWXBQE8foMUQ4ArboPkRgAPoVCbnp9wCciVAB5GCQn1a1NRXfbXXEJyJ0BT4Rry5j+7DsilAGmyaQIw6GKU4xLKRZcRGHQwsqiCLiZ2TCjPo6u11AjkPaO+66Y1Ymiz2gR5llxZ1Km4c3d2TaqJBRgNq8cI9HuOk/9WxOpR1wlU/0dntxNJZgRy13UK82wfo7zKX/K6IIHsdGlQLMouBn3U7aQ2YA99zC5tGOUMgzwl0JUM9viKP7sglNOpJ95qWPjiwlg9kQWrx8KzfX8EahiGYRiGYbhIfgJD9pv3hnQZlwAAAABJRU5ErkJggg=="
												alt="downloading-updates"
												class="w-6 h-6"
											/>
											<div class="text-ihh-slate-gray hover:text-ihh-gray cursor-pointer font-semibold text-sm tracking-wide">Order Form</div>
										</button>
									</div>
									<!-- Out of Stock -->
									<div>
										<!-- Is this your kit?  -->
										<div v-if="!isKitClaimed" class="flex w-full justify-center md:justify-start">
											<div class="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
												<div class="text-ihh-midnight-gray font-medium">Is this your kit?</div>
												<button
													@click="claimKit"
													class="text-ihh-white bg-ihh-dark-salmon hover:bg-ihh-salmon px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200"
												>
													Claim as midwife
												</button>
											</div>
										</div>
										<!-- Edit My Kit -->
										<div v-if="isKitClaimed && isKitOwner" class="flex w-full justify-center md:justify-start">
											<div class="flex flex-col md:flex-row items-center gap-2 w-full md:justify-start md:gap-4">
												<router-link
													:to="editPath"
													class="text-ihh-white bg-ihh-dark-salmon border-2 border-ihh-dark-salmon hover:border-ihh-salmon hover:bg-ihh-salmon px-4 sm:px-6 md:px-10 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 w-full md:w-auto text-center"
												>
													{{ editKitButtonText }}
												</router-link>
												<router-link
													to="/account/birth-kits"
													class="text-ihh-midnight-gray border-2 border-ihh-dark-salmon hover:bg-ihh-dark-salmon px-3 sm:px-5 md:px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 w-full md:w-auto text-center"
												>
													Manage Birth Kits
												</router-link>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<!-- Claim as midwife -->
						<button
							v-if="hasOutOfStockItems"
							@click="scrollToOutOfStockItems"
							class="flex w-full max-w-52 justify-center items-center text-center text-ihh-white text-xs font-semibold bg-ihh-coral rounded py-2 capitalize"
						>
							Kit contains out of stock items
						</button>

						<div class="flex w-full justify-between py-3">
							<div class="flex w-full justify-start text-start text-lg font-medium md:text-2xl tracking-wide text-ihh-midnight-gray m-auto whitespace-nowrap">
								Included in your kit
							</div>
							<div class="flex w-full justify-end max-w-36 sm:max-w-xs">
								<button
									@click="resetQuantities"
									class="group flex-row w-full m-auto items-center justify-center text-center text-xs sm:text-sm font-semibold text-ihh-midnight-gray hover:text-ihh-white border-3 border-ihh-dark-salmon px-4 py-3 rounded-lg hover:bg-ihh-dark-salmon duration-100"
								>
									Reset quantities
								</button>
							</div>
						</div>

						<div class="grid grid-cols-1 sm:grid-cols-2 landscape-mobile2:grid-cols-1 landscape-mobile3:grid-cols-1 landscape-mobile3:px-8 gap-4">
							<BirthKitItem
								v-for="p in kit.products?.filter((p) => !p?.optional)"
								:key="p.product.id"
								:product="p.product"
								:variant="p.variant"
								:quantity="p.quantity"
								:isBuilder="false"
								@update:quantity="p.quantity = $event"
								@update:variant="p.variant = $event ? JSON.parse(JSON.stringify($event)) : null"
							/>
						</div>

						<div v-if="optionalProducts?.length">
							<div class="flex w-full justify-between py-6">
								<div
									class="flex w-full justify-start text-start text-lg md:text-2xl tracking-wide text-ihh-midnight-gray font-medium m-auto whitespace-nowrap"
								>
									Optional Items
								</div>
							</div>

							<div class="grid grid-cols-1 sm:grid-cols-2 landscape-mobile2:grid-cols-1 landscape-mobile3:grid-cols-1 landscape-mobile3:px-8 gap-4">
								<BirthKitItem
									v-for="p in optionalProducts"
									:key="p.product.id"
									:product="p.product"
									:variant="p.variant"
									:quantity="p.quantity"
									:isBuilder="false"
									@update:quantity="p.quantity = $event"
									@update:variant="p.variant = $event"
								/>
							</div>
						</div>

						<div id="outOfStockItems" v-if="hasOutOfStockItems">
							<div class="flex w-full justify-between py-6">
								<div class="flex w-full justify-start text-start text-lg md:text-2xl tracking-wide text-ihh-midnight-gray m-auto whitespace-nowrap">
									Out of Stock Items
								</div>
							</div>

							<div class="grid grid-cols-1 sm:grid-cols-2 landscape-mobile2:grid-cols-1 landscape-mobile3:grid-cols-1 landscape-mobile3:px-8 gap-4">
								<BirthKitItem
									v-for="p in outOfStockItems"
									:key="p.product.id"
									:product="p.product"
									:variant="p.variant"
									:quantity="p.quantity"
									:isBuilder="false"
									@update:quantity="p.quantity = $event"
									@update:variant="p.variant = $event"
								/>
							</div>
						</div>
						<!-- End Of The Birth Kit Items -->

						<p class="mt-12 text-xs text-ihh-slate-gray/80 leading-relaxed text-center max-w-4xl mx-auto px-2">
							Custom birth kits listed on InHisHands.com are created at the request of individuals and do not represent an endorsement of any person, practice,
							or service. In His Hands does not verify the qualifications, licensure, or credentials of any individual associated with a custom birth kit. We
							always encourage our customers to do their own research and confirm that their care provider meets their personal needs and applicable
							professional&nbsp;standards.
						</p>

						<!-- <p class="text-center pt-16">
							Need to order more than 1 kit? Email us at <a class="text-ihh-dark-salmon underline" href="mailto:help@inhishands.com">help@inhishands.com</a>
							for further assistance.
							<br /><br />
						</p> -->
					</div>
				</div>

				<div v-if="kit?.id" class="flex w-full justify-center sticky bottom-0 z-10">
					<div
						class="flex w-full justify-center m-auto items-center w-full bg-ihh-white shadow-md px-4 py-2 landscape-mobile2:py-1 landscape-mobile3:!py-0 sm:py-4 rounded border border-ihh-gray/50 cursor-pointer"
					>
						<div class="flex w-full max-w-8xl justify-between">
							<div class="flex flex-col w-full justify-between hidden sm:block landscape-mobile:hidden">
								<div class="landscape-mobile2:text-sm text-lg lg:text-2xl font-semibold text-ihh-midnight-gray max-w-md tracking-wide mr-6">
									{{ kit.name }}
								</div>
								<div v-if="hasOutOfStockItems" class="flex w-full justify-start">
									<button
										class="mt-2 landscape-mobile3:mt-0 landscape-mobile3:mb-1 landscape-mobile2:text-2xs text-xs text-center bg-ihh-coral py-1 px-3 text-ihh-white font-bold capitalize rounded-md"
									>
										Kit contains out of stock items
									</button>
								</div>
							</div>
							<div class="flex w-full justify-between sm:justify-end items-center landscape-mobile:justify-between landscape-mobile2:justify-between">
								<div class="flex-col text-ihh-midnight-gray text-center md:text-end mr-6">
									<div class="flex sm:flex-col md:flex-row landscape-mobile2:flex-row landscape-mobile:flex-row">
										<div class="landscape-mobile2:text-sm text-base xs:text-base lg:text-lg font-semibold mr-2">SUBTOTAL:</div>
										<div class="landscape-mobile2:text-sm text-base xs:text-base lg:text-lg font-semibold tracking-widest">
											{{ subtotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
										</div>
									</div>

									<a @click="cartBirthKit" class="flex w-full justify-between whitespace-nowrap">
										<div class="max-w-32 flex w-full">
											<span class="flex w-full justif-end">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 18 18"
													width="16"
													height="16"
													stroke-width="1.75"
													stroke-linecap="round"
													stroke-linejoin="round"
													class="p-0 mr-1"
												>
													<path
														d="M9 15.75V15.75C5.27175 15.75 2.25 12.7283 2.25 9V9C2.25 5.27175 5.27175 2.25 9 2.25V2.25C12.7283 2.25 15.75 5.27175 15.75 9V9C15.75 12.7283 12.7283 15.75 9 15.75Z"
														class="fill-none stroke-ihh-dark-salmon hover:stroke-ihh-dark-salmon"
													/>
													<path d="M9 12.75V9H8.25" class="fill-none stroke-ihh-dark-salmon hover:stroke-ihh-dark-salmon" />
													<path
														d="M8.81175 6C8.70825 6 8.62425 6.084 8.625 6.1875C8.625 6.291 8.709 6.375 8.8125 6.375C8.916 6.375 9 6.291 9 6.1875C9 6.084 8.916 6 8.81175 6"
														class="sfill-none stroke-ihh-dark-salmon hover:stroke-ihh-dark-salmon"
													/>
												</svg>
											</span>
											<button @click="shippingModalOpen = true" class="text-ihh-dark-salmon hover:text-ihh-salmon underline text-xs font-semibold">
												Shipping and returns
											</button>
										</div>
									</a>
								</div>
								<div class="flex items-center">
									<button
										@click="addToBag"
										:disabled="addingToBag"
										class="items-center text-sm text-ihh-midnight-gray border-ihh- bg-ihh-dark-salmon hover:bg-ihh-salmon transition-all rounded-lg px-4 sm:px-6 py-2 sm:py-2 lg:py-4 font-semibold text-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{{ addingToBag ? 'Adding...' : 'Add this kit to my bag' }}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</Mango>

		<Modal v-if="shippingModalOpen" @hide="shippingModalOpen = false">
			<div class="prose">
				<h2>Shipping and Returns</h2>
				<p>Shipping cost is based on weight. Just add products to your bag and use the Shipping Calculator to view the different options.</p>
				<p>We offer free shipping on orders over $125 shipping to the lower 48 states</p>
				<p>We want you to be 100% satisfied with your purchase. Most items in new condition can be returned or exchanged within 90 days of purchase.</p>
			</div>
		</Modal>

		<Modal v-if="upsellModalOpen" @hide="upsellModalOpen = false" max-width="max-w-4xl" :full-screen-mobile="true" v-slot="{ close }">
			<div class="text-center prose mx-auto">
				<h2 class="my-4">The kit has been added to your&nbsp;bag.</h2>
				<div class="flex space-x-8 justify-center">
					<button @click="(close(), (store.showBag = true))" class="px-3 py-2 rounded-lg border-3 border-ihh-dark-salmon" href="/cart.php">View Bag</button>
					<button @click="close" class="px-3 py-2 bg-ihh-dark-salmon hover:bg-ihh-salmon transition-all rounded-lg text-ihh-midnight-gray font-semibold">
						Continue Shopping
					</button>
				</div>
				<h4 class="">You may also like these:</h4>
			</div>
			<div class="pb-20 sm:pb-0">
				<div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
					<Product v-for="item in upsellItems" :key="item.id" :product="item" :clampName4="true" />
				</div>
			</div>
		</Modal>

		<Modal v-if="showLogin" max-width="max-w-4xl">
			<Login @loggedIn="claimKit" @hide="showLogin = false" :small="true" />
		</Modal>

		<RequiredOptionsModal
			v-if="showRequiredOptionsModal"
			:products="productsNeedingOptions"
			@hide="showRequiredOptionsModal = false"
			@confirm="onRequiredOptionsConfirmed"
		/>
	</div>
</template>

<script>
import Product from '../products/product.vue'
import BirthKitItem from '../products/birth-kit-item.vue'
import Swal from 'sweetalert2'
import Modal from '../layout/modal.vue'
import Mango from '../../helpers/mango'
import axios from 'axios'
import Login from '../layout/login.vue'
import RequiredOptionsModal from '../partials/required-options-modal.vue'
import { validateSession, clearSession } from '../../helpers/session'

export default {
	components: { Product, BirthKitItem, Modal, Login, RequiredOptionsModal },
	inject: ['store'],
	data() {
		return {
			showCustomBKMessage: true,
			showLogin: false,
			upsellItems: [],
			shippingModalOpen: false,
			upsellModalOpen: false,
			showRequiredOptionsModal: false,
			productsNeedingOptions: [],
			originalKit: null,
			kit: null,
			kitLoadError: false,
			hasOutOfStockItems: false, // Boolean indicating if the kit had out-of-stock items before filtering
			outOfStockItems: [], // Array of out-of-stock items from the kit before filtering
			cartIsEmpty: false,
			cartIsEmptyModal: false,
			removeBirthKit: false,
			birthKitToRemove: null,
			activeForm: null,
			pages: [1, 2, 3, 4],
			selectedPage: 1,
			cart: false,
			showMenu: false,
			loading: false,
			addingToBag: false,
			kitImageError: false,
			query: {
				depthLimit: 2,
				fields: ['id', 'name', 'image', 'products', 'products.product', 'products.variant', 'products.quantity', 'products.optional'],
			},
			graphql: `
                query {
                    birthKit(id: "${this.$route.params.id}") {
                        id
                        name
                        image {
                            url
                        }
                        inventoryLevel
                        inventoryTracking
                        products {
                            product {
                                id
                                name
                                currentPrice
                                inventoryLevel
                                availability
                                outOfStock
                                bulkPricingRules {
                                    quantityMin
                                    quantityMax
                                    type
                                    amount
                                }
                                images {
                                    url
                                }
                                productOptions {
                                    id
                                    name
                                    displayName
                                    sortOrder
                                    optionValues {
                                        id
                                        label
                                        isDefault
                                        valueData {
                                            imageUrl
                                        }
                                    }
                                }
                                variants {
                                    id
                                    purchasingDisabled
                                    inventoryLevel
                                    currentPrice
                                    optionValues {
                                        id
                                        label
                                        optionId
                                        optionDisplayName
                                    }
                                }
                            }
                            variant {
                                id
                                purchasingDisabled
                                inventoryLevel
                                currentPrice
                                optionValues {
                                    id
                                    label
                                    optionId
                                    optionDisplayName
                                }
                            }
                            quantity
                            optional
                        }
                    }
                }
            `,
		}
	},
	async created() {
		let upsellIds = [862, 4069, 2022, 3338, 3681, 3813, 1926, 3064, 3224, 3263, 3337, 3544]
		// this.upsellItems = await Mango.products({ search: { bcId: { $in: upsellIds } }, limit: 12 })

		try {
			const upsellItems = await axios.get(`${this.store.api}/controllers/bigCommerce/birthKits/upsellProducts`)
			this.upsellItems = upsellItems?.data
		} catch (error) {
			console.log('Error fetching birthKit upsell items')
		}
	},

	mounted() {
		const showMessage = window.localStorage.getItem('showCustomBKMessage')
		if (showMessage === 'false') this.showCustomBKMessage = false
	},

	methods: {
		hideCustomBKMessage() {
			this.showCustomBKMessage = false
			window.localStorage.setItem('showCustomBKMessage', 'false')
		},
		scrollToOutOfStockItems() {
			document.getElementById('outOfStockItems').scrollIntoView({ behavior: 'smooth' })
		},
		onKitLoadError() {
			this.kitLoadError = true
		},
		setKit(kit) {
			this.kitLoadError = false
			if (!kit) {
				this.$router.push('/404')
				return
			}
			if (!kit?.products) {
				this.kit = kit
				return
			}
			if (!kit?.name) {
				document.title = 'Custom Birth Kit By Midwife'
			} else {
				document.title = kit.name
			}
			kit = kit?.birthKit || kit

			kit.products = kit.products?.filter((p) => !!p.product)

			const unfilteredKit = JSON.parse(JSON.stringify(kit))
			this.hasOutOfStockItems = this.kitHasOutOfStockProducts(unfilteredKit)
			this.outOfStockItems = this.getOutOfStockProducts(unfilteredKit) || []

			// Filter out out-of-stock products
			const filteredProducts = kit.products
				.filter((item) => !this.productAndVariantsOutOfStock(item.product))
				.sort((a, b) => a.product?.name?.localeCompare(b.product?.name))
			this.kit = JSON.parse(JSON.stringify(kit))
			delete this.kit.products
			this.kit.products = filteredProducts

			// Store the original filtered kit
			this.originalKit = JSON.parse(JSON.stringify(this.kit))
		},
		resetQuantities() {
			Swal.fire({
				title: 'Are you sure?',
				text: 'This will reset all quantities to their original values',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Yes, reset',
				cancelButtonText: 'No, cancel',
			}).then((result) => {
				if (result.isConfirmed) {
					this.kit.products.forEach((p) => {
						p.quantity = this.originalKit.products.find((op) => op.product.id === p.product.id).quantity
					})
				}
			})
		},
		async addToBag() {
			if (this.addingToBag) return
			this.addingToBag = true

			// Check if this kit was already added to the cart
			if (this.store.cart.kitsAdded?.some((k) => k.kitId === this.kit.id)) {
				const result = await Swal.fire({
					title: 'Kit Already in Bag',
					text: 'This kit has already been added to your bag. Would you like to add it again?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Yes, add again',
					cancelButtonText: 'No, cancel',
					confirmButtonColor: '#e59789',
				})
				if (!result.isConfirmed) {
					this.addingToBag = false
					return
				}
			}
			if (!this.kit?.products?.some((p) => p.quantity > 0)) {
				Swal.fire({
					title: 'No Products Quantities Selected',
					text: 'Please set a quantity greater than zero for at least one product to add to your bag.',
					icon: 'warning',
					confirmButtonText: 'Return to kit',
					confirmButtonColor: '#e59789',
				})
				this.addingToBag = false
				return
			}

			// Resolve variants from default options for products that don't have a variant set yet
			// This handles race conditions where the BirthKitItem component hasn't emitted the variant yet
			for (const p of this.kit.products) {
				if (!p.variant?.id && p.product?.productOptions?.length && p.quantity > 0) {
					console.log('missing variant: ', p.product.name)
					const defaultOptions = p.product.productOptions
						.map((po) => {
							const defaultOption = po.optionValues?.find((o) => o.isDefault)
							if (defaultOption) {
								return { ...defaultOption, optionId: po.id }
							}
							return null
						})
						.filter(Boolean)

					// Only set variant if all product options have a default
					if (defaultOptions.length === p.product.productOptions.length) {
						p.variant = p.product.variants?.find((v) => v.optionValues?.every((vo) => defaultOptions.find((d) => d.id === vo.id)))
					}
				}
			}

			if (!this.requiredOptionsSelected?.value) {
				this.productsNeedingOptions = this.kit.products.filter((p) => p.quantity > 0 && p.product?.productOptions?.length && !p.variant?.id)
				this.showRequiredOptionsModal = true
				this.addingToBag = false
				return
			}

			let products = JSON.parse(JSON.stringify(this.kit.products))
			products = products.map((p) => ({ ...p, group: this.kit.id, item: p.product }))?.filter((p) => p.quantity > 0)

			// this.store.cart.cartItems = [...this.store.cart.cartItems, ...products] // update any existing items in the cart vs possibly adding duplicates

			let cartItems = [...this.store.cart.cartItems]

			for (const kitItem of products) {
				const matchIndex = cartItems.findIndex((cartItem) => {
					const productMatch = cartItem.product?.bcId === kitItem.product?.bcId
					const kitHasVariant = kitItem?.variant && kitItem?.variant?.bcId
					const cartHasVariant = cartItem?.variant && cartItem?.variant?.bcId

					if (productMatch) {
						if (kitHasVariant || cartHasVariant) {
							// Both must have variant.bcId and match
							return kitItem.variant?.bcId && cartItem.variant?.bcId && kitItem.variant.bcId === cartItem.variant.bcId
						}
						// Neither has variant, or only product match matters
						return true
					}
					return false
				})

				if (matchIndex !== -1) {
					// Merge quantities, use kit item (preserving group)
					const mergedItem = { ...kitItem, quantity: kitItem.quantity + (cartItems[matchIndex].quantity || 0) }
					cartItems.splice(matchIndex, 1, mergedItem)
				} else {
					cartItems.push(kitItem)
				}
			}

			// GA4 Add to Cart Event
			let ga4Products = products.map((p) => ({
				id: p.product?.sku,
				name: p.product?.name,
				quantity: p.quantity,
				google_business_vertical: 'retail',
				currency: 'USD',
				price: p.product?.currentPrice,
			}))

			if (typeof gtag === 'function') {
				gtag('event', 'add_to_cart', {
					send_to: 'AW-387509916',
					ecomm_pagetype: 'cart',
					currency: 'USD',
					items: ga4Products,
					value: ga4Products.reduce((total, product) => total + product.price * product.quantity, 0),
				})
			}

			this.store.cart.cartItems = cartItems
			// this.store.showBag = true
			this.store.cart.staffNote = `Birth Kit: "${this.kit.name}".`
			this.store.cart.kitId = this.kit.id

			// Track this kit add event
			if (!this.store.cart.kitsAdded) this.store.cart.kitsAdded = []
			this.store.cart.kitsAdded.push({ time: new Date(), kitId: this.kit.id, kitName: this.kit.name })

			console.log('staff note added: ', this.store.cart.staffNote)

			this.upsellItems = this.filterProducts(this.upsellItems)?.slice(0, 12)

			this.addingToBag = false
			this.upsellModalOpen = true

			// Swal.fire({
			// 	title: 'The kit has been added to your bag.',
			// 	icon: 'success',
			// 	html: `
			//     <div class="ob-success-actions">
			//         <a class="ob-bulk-view-cart button button--primary" href="/cart.php">View Bag</a>
			//         <a class="ob-bulk-continue button button--secondary">Continue Shopping</a>
			//     </div>
			//     <h4 class="ob-upsell-products-title">You may also like these:</h4>
			//     <div class="ob-upsell-products product">${this.upsellProductsHTML}</div>`,
			// 	width: '55em;',
			// 	showCancelButton: false,
			// 	showConfirmButton: false,
			// 	onOpen: () => this.bindSuccessCartButtons(),
			// })
		},
		onRequiredOptionsConfirmed(resolvedProducts) {
			this.showRequiredOptionsModal = false
			// Apply resolved variants and quantities back to the kit products
			for (let resolved of resolvedProducts) {
				let kitProduct = this.kit.products.find((p) => p.product.id === resolved.product.id)
				if (kitProduct) {
					if (resolved.variant) kitProduct.variant = resolved.variant
					kitProduct.quantity = resolved.quantity
				}
			}
			// Re-run addToBag now that all options are selected
			this.addToBag()
		},
		downloadContentOnPage() {
			localStorage.setItem('kit', JSON.stringify(this.kit))

			// Open the new window
			window.open('/birth-kit-order-form', '_blank')
		},
		async claimKit() {
			const session = await validateSession({ store: this.store, axios })
			if (!session.valid) {
				this.showLogin = true
				return
			}

			// swal alert to confirm claiming
			const result = await Swal.fire({
				title: 'For Midwives Only',
				text: 'Are you sure you want to claim this kit?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Claim',
				cancelButtonText: 'Cancel',
				confirmButtonColor: '#e59789',
			})

			if (result.isConfirmed) {
				try {
					const response = await axios.post(`${this.store.api}/controllers/birthKit/claim`, {
						kitId: this.kit.id,
					})

					let data = response?.data
					if (data?.authorized === false || data?.valid === false) {
						if (response.status === 401) {
							clearSession(this.store, axios)
							this.showLogin = true
						}
						await Swal.fire({ text: data?.response || 'There was an error submitting your claim.', icon: 'error' })
						return
					}

					await Swal.fire({ text: 'Your claim request has been sent. Our team will review it and contact you soon.', icon: 'success' })
				} catch (error) {
					console.error('Error claiming kit:', error)
					let data = error.response?.data
					if (error.response?.status === 401) {
						clearSession(this.store, axios)
						this.showLogin = true
					}
					await Swal.fire({
						text: data?.response || 'There was an error submitting your claim. Please try again later or contact support.',
						icon: 'error',
					})
				}
			}
		},
		variantsOutOfStock(product) {
			return product?.variants?.every((v) => v.inventoryLevel <= 0) && product.inventoryTracking === 'variant'
		},
		productAndVariantsOutOfStock(product) {
			return product?.outOfStock || this.variantsOutOfStock(product) || !product?.isVisible
		},
		kitHasOutOfStockProducts(kit) {
			return kit?.products?.some((p) => this.productAndVariantsOutOfStock(p.product))
		},
		getOutOfStockProducts(kit) {
			return kit?.products?.filter((p) => this.productAndVariantsOutOfStock(p.product))
		},
		filterOutOfStock(products) {
			return products.filter((product) => {
				if (product.inventoryTracking !== 'variant') {
					if (!product.outOfStock) return true
					return false
				}

				return !product.variants?.every((v) => v.inventoryLevel <= 0)
			})
		},

		filterProducts(products) {
			// Get product IDs that are already in the cart
			const productIdsInCart = this.store.cart?.cartItems?.map?.((c) => c.item.bcId) || []

			// Filter out products that are already in the cart and out of stock products
			return this.filterOutOfStock(products.filter((product) => !productIdsInCart.includes(product.bcId)))
		},
	},
	computed: {
		showKitImage() {
			const url = this.kit?.image?.url
			if (!url) return false
			if (url.includes('in-his-hands-logo.png')) return false
			if (this.kitImageError) return false
			return true
		},
		editing() {
			return this.$route.path.includes('/edit')
		},
		editPath() {
			return `${this.$route.path}${this.$route.path.endsWith('/') ? '' : '/'}edit`
		},
		optionalProducts() {
			return this.kit?.products?.filter((p) => p.optional && p.product?.id)
		},
		outOfStock() {
			// return this.kit?.products?.some((p) => {
			// 	p.product?.outOfStock || (p.product.inventoryTracking === 'variant' && p.variant?.inventoryLevel <= 0)
			// })
			return this.kit?.products?.some((p) => {
				return this.productAndVariantsOutOfStock(p.product)
			})
		},
		outOfStockProducts() {
			return this.kit?.products?.filter((p) => {
				return this.productAndVariantsOutOfStock(p.product)
			})
		},
		// Subtotal calculation (total before tax)
		subtotal() {
			return this.kit?.products?.reduce((total, p) => {
				let price = p.variant?.currentPrice || p.product?.currentPrice
				let rules = p.product?.bulkPricingRules
				// if (rules?.length) console.log('rules', rules)
				for (let rule of rules) {
					// console.log('rule', rule)
					if (p.quantity >= rule.quantityMin && (p.quantity <= rule.quantityMax || !rule.quantityMax)) {
						// price = rule.type === 'fixed' ? rule.amount : rule.type === 'price' ? price - rule.amount : price * rule.amount

						// fix for bulk pricing rule with type percent:
						price =
							rule.type === 'fixed'
								? rule.amount
								: rule.type === 'price'
									? price - rule.amount
									: rule.type === 'percent'
										? price * (1 - rule.amount / 100)
										: price

						console.log('price', price)
					}
				}
				return total + price * p.quantity
			}, 0)
		},
		// Texas State Sales Tax (8.21% of the subtotal)
		texasStateSalesTax() {
			const taxRate = 0.0821
			return this.subtotal * taxRate
		},
		// Total price including the tax
		totalPrice() {
			const totalWithTax = parseFloat(this.subtotal) + parseFloat(this.texasStateSalesTax)
			return totalWithTax // Return the total with tax formatted as a string
		},
		requiredOptionsSelected() {
			let productsWithMissingOptions = []
			let value = true
			this.kit.products
				.filter((p) => p.quantity > 0)
				.forEach((p) => {
					if (p.product?.productOptions?.length) {
						if (!p.variant?.id) {
							value = false
							productsWithMissingOptions.push(p?.product?.name)
						}
					}
				})
			return {
				value,
				products: productsWithMissingOptions,
			}
		},
		isKitOwner() {
			return this.store?.user?.id === this.kit?.authorId || this.store?.user?.roles?.includes('admin')
		},
		editKitButtonText() {
			return this.store?.user?.id === this.kit?.authorId ? 'Edit My Kit' : 'Manage Kit'
		},
		isKitClaimed() {
			return !!this.kit?.authorId
		},
	},
}
</script>

<style scoped>
.charm-regular {
	font-family: 'Charm', cursive;
	font-weight: 400;
	font-style: normal;
}

.charm-bold {
	font-family: 'Charm', cursive;
	font-weight: 700;
	font-style: normal;
}
</style>
